// upload-avatar — agent SPA 专用小文件上传(头像场景)。
//
// 仅走 Path A(POST /gapi/upload/small):头像直接同步上传,不按 size 路由、
// 不需要 Path B b2 multipart。头像 spec §3 限制 5MB,与 Path A 上限(单分片 5MB)一致。
//
// 与 user/_lib/upload.ts 区别:
//   - user 端有 Path A + Path B 双路径(文件管理通用上传);
//   - 此处只保留 uploadSmall 的最小子集,保持 agent 端代码体积。
//
// 用法:
//   const fileId = await uploadAvatarSmall(file)
//   await setAvatarMutation.mutateAsync(fileId)
//
// 头像 mime / size 校验在调用方做(UI 即时反馈),本 helper 只做网络请求。
import { getSupabase } from '@dogdisk/shared'

export type UploadSmallResp = {
  file_info_id: string
  base_file_id: string
  sha256: string
  size: number
  status: string
}

/**
 * uploadAvatarSmall — 走 `POST /gapi/upload/small` 上传小文件,返 file_info_id。
 *
 * 注意:
 *  - apiClient 默认 JSON;multipart 必须绕开,这里直接 fetch + supabase Bearer。
 *  - 不带 parent_id(上传后 server copy 进隐藏系统树 system_avatar,自动路由)。
 *  - 失败直接 throw,调用方处理 error / loading state。
 */
export async function uploadAvatarSmall(file: File): Promise<string> {
  const form = new FormData()
  form.append('file', file, file.name)
  form.append('filename', file.name)
  if (file.type) form.append('mime_type', file.type)

  const sb = getSupabase()
  const { data: session } = await sb.auth.getSession()
  const token = session.session?.access_token ?? ''

  const res = await fetch('/gapi/agent/upload/small', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
    body: form,
  })
  const text = await res.text()
  const json: unknown = text ? JSON.parse(text) : undefined
  if (!res.ok) {
    const msg =
      json && typeof json === 'object' && 'message' in json
        ? String((json as { message: unknown }).message)
        : res.statusText
    throw new Error(msg)
  }
  const resp = json as UploadSmallResp
  return resp.file_info_id
}
